import React from 'react';

import AreYouSureModal from '../../form-components/AreYouSureModal.component';
import DotdotdotButton from '../../form-components/DotdotdotButton.component';
import Box from '@mui/material/Box';

import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import OkCancelModal from '../../form-components/OkCancelModal.component';
import {uuid} from '../../../utils/utils';
import {PsfCategory} from '../../../@types/psfTemplate';

export default function PsfCategoryActionsMenu(props: {
    category: PsfCategory,
    existingNames: string[],
    existingSubs: string[]|undefined,
    addSubCategory: (parent: PsfCategory|null, category: string) => void,
    renameCategory: (newCat: PsfCategory) => void,
    deleteCategory: (cat: PsfCategory) => void
}): React.ReactElement {
    const {category, existingNames, existingSubs, addSubCategory, renameCategory, deleteCategory} = props;
    const [confirmDeleteCategory, setConfirmDeleteCategory] = React.useState<boolean>(false);
    const [openAddSubCategory, setOpenAddSubCategory] = React.useState<boolean>(false);
    const [openRenameCategory, setOpenRenameCategory] = React.useState<boolean>(false);
    const [subCategory, setSubCategory] = React.useState<string>('');
    const [newName, setNewName] = React.useState<string>('');
    const uuidRef = React.useRef(uuid());
    const [error, setError] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');

    React.useEffect(() => {
        if (category) {
            setNewName(category.name);
        }
    }, [category]);

    function onAddSubCategory(): void {
        setOpenAddSubCategory(true);
    }

    function onRenameCategory(): void {
        setOpenRenameCategory(true);
    }

    function onDeleteCategory(): void {
        setConfirmDeleteCategory(true);
    }

    const updateSubCategory = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSubCategory(event.target.value);
        (category.path.split('::').find((name) => name == event.target.value) !== undefined ||
            existingSubs?.find((sub) => sub == event.target.value) !== undefined) ? alreadyExistError() : clearError();
    }

    const updateCategoryName = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setNewName(event.target.value);
        existingNames.find((name) => name == event.target.value) === undefined ?
            clearError()
            :
            alreadyExistError();
    }
    
    const alreadyExistError = (): void => {
        setError(true);
        setErrorMessage('Category name already exists.');
    }
    
    const clearError = (): void => {
        setError(false);
        setErrorMessage('');
    }

    return (
        <Box tabIndex={0} >
            <DotdotdotButton>
                <MenuItem id={`add-sub-${uuidRef.current}`} onClick={onAddSubCategory}>Add SubCategory</MenuItem>
                <MenuItem id={`rename-cat-${uuidRef.current}`} onClick={onRenameCategory}>Rename</MenuItem>
                <MenuItem id={`delete-cat-${uuidRef.current}`} onClick={onDeleteCategory}>Delete</MenuItem>
            </DotdotdotButton>

            <AreYouSureModal
                open={confirmDeleteCategory}
                title={`Delete Category "${category.path.replaceAll('::', ' > ')}"?`}
                onConfirm={(): void => { deleteCategory(category); setConfirmDeleteCategory(false); }}
                onClose={(): void => {
                    setConfirmDeleteCategory(false);
                }}
            >
                <Typography variant='body1'>Are you sure you want to delete this category?</Typography>
                <Typography variant='body2'>Note: This will delete any and all associated subcategories / items. If there any presences associated with those items, they will be deleted as well.</Typography>
            </AreYouSureModal>

            <OkCancelModal
                open={openAddSubCategory}
                title={`Add sub category to ${category.path.replaceAll('::', ' > ')}:`}
                onClose={(): void => { setOpenAddSubCategory(false); }}
                okButtonText='Add SubCategory'
                onConfirm={(): void => { addSubCategory(category, subCategory); setSubCategory(''); setOpenAddSubCategory(false); }}
                okToConfirm={(): boolean => !error && subCategory !== ''}
            >
                <TextField
                    autoFocus
                    margin='dense'
                    id='sub category name'
                    label='Sub Category'
                    variant='filled'
                    size='small'
                    defaultValue={subCategory}
                    onChange={updateSubCategory}
                    error={error}
                    helperText={errorMessage}
                    fullWidth
                />
            </OkCancelModal>

            <OkCancelModal
                open={openRenameCategory}
                title={`Rename ${category.path.replaceAll('::', ' > ')}:`}
                onClose={(): void => { setOpenRenameCategory(false); }}
                okButtonText='Rename'
                onConfirm={(): void => { renameCategory({...category, name: newName }); setNewName(''); setOpenRenameCategory(false); }}
                okToConfirm={(): boolean => !error && newName !== ''}
            >
                <TextField
                    autoFocus
                    margin='dense'
                    id='category name'
                    label='Category Name'
                    variant='filled'
                    size='small'
                    defaultValue={newName}
                    onChange={updateCategoryName}
                    error={error}
                    helperText={errorMessage}
                    fullWidth
                />
            </OkCancelModal>
        </Box>
    );
}

